/**
 * Generators for the patient, primary-coverage, and provider detail
 * cards shown on the single-order page sidebar.
 *
 * @module singleOrderPageDataGenerator
 */
import {
  GeneralOrderDetails,
  RequestingPractitionerDetails,
  SingleCoverage,
} from "../../../../data-model/medicalPaOrdersModel";
import { formatDateToUTCWithFormat } from "../../../../utils/formatDate";
import { generateFullName } from "../../../../utils/generateOrderDataForTable";
import { capitalizeString } from "../../../../utils/stringModifications";

/**
 * Builds the primary-coverage detail rows (plan name, service type,
 * date of service, regimen) from coverage and order data.
 *
 * @param coverages - All coverage entries for the order
 * @param generalOrderDetails - Postgres-backed general order details
 * @returns Array of `{ headerText, subText }` pairs
 */
export const generatePrimaryDetails = (
  coverages: SingleCoverage[],
  generalOrderDetails: GeneralOrderDetails,
) => {
  const coverage = coverages.find(
    (coverage) => coverage.coverage_type === "primary",
  );
  const primaryData = [
    {
      headerText: "Plan Name",
      subText: coverage?.plan_name ?? "",
    },
    {
      headerText: "Service Type",
      subText: generalOrderDetails?.payload?.service_type?.join(", ") ?? "",
    },
    {
      headerText: "Date of Service",
      subText: formatDateToUTCWithFormat(
        generalOrderDetails?.payload?.date_of_service ?? "",
        "MM/DD/YYYY",
        "",
      ),
    },
    {
      headerText: "Regimen",
      subText: generalOrderDetails?.payload?.regimen_name ?? "",
    },
  ];

  return primaryData;
};

/**
 * Builds patient detail rows (name, MRN, DOB, member ID) from
 * general order details and coverage data.
 *
 * @param generalOrderDetails - Postgres-backed general order details
 * @param coverage - All coverage entries for the order (primary is extracted internally)
 * @returns Array of `{ header, text }` pairs
 */
export const generatePatientDetails = (
  generalOrderDetails: GeneralOrderDetails,
  coverage: SingleCoverage[],
) => {
  const primaryCoverage = coverage.find(
    (coverage) => coverage.coverage_type === "primary",
  );
  const patientData = [
    {
      header: "Patient Name",
      text: capitalizeString(
        generateFullName(
          generalOrderDetails?.demographics?.first_name ?? "",
          "",
          generalOrderDetails?.demographics?.last_name ?? "",
        ),
      ),
    },
    { header: "MRN ID", text: generalOrderDetails?.demographics?.patient_id },
    {
      header: "DOB",
      text: formatDateToUTCWithFormat(
        generalOrderDetails?.demographics?.date_of_birth ?? "",
        "MM/DD/YYYY",
        "",
      ),
    },
    {
      header: "Member ID",
      text: primaryCoverage?.member_id,
    },
  ];

  return patientData;
};

/**
 * Builds requesting-provider detail rows (name, NPI, TIN, contact)
 * from the practitioner details object.
 *
 * @param requestingPractitionerDetails - Practitioner data from the submission payload
 * @returns Array of `{ header, text }` pairs
 */
export const generateProviderDetails = (
  requestingPractitionerDetails?: RequestingPractitionerDetails,
) => {
  const providerData = [
    {
      header: "Name",
      text: generateFullName(
        requestingPractitionerDetails?.first_name ?? "",
        "",
        requestingPractitionerDetails?.last_name ?? "",
      ),
    },
    {
      header: "NPI",
      text: requestingPractitionerDetails?.npi ?? "",
    },
    {
      header: "TIN",
      text: requestingPractitionerDetails?.tax_id ?? "",
    },
    {
      header: "Contact No",
      text: requestingPractitionerDetails?.phone ?? "",
    },
  ];

  return providerData;
};
